import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {CnCreateStatementRequestDto} from '../../../model/dto/api/cn-create-statement-request.dto';
import {CnAddStatementLineRequestDto} from '../../../model/dto/api/cn-add-statement-line-request.dto';
import {CnRunReconciliationRequestDto} from '../../../model/dto/api/cn-run-reconciliation-request.dto';
import {CnReconciliationResultDto} from '../../../model/dto/api/cn-reconciliation-result.dto';
import {CnAbgleichRequestDto} from '../../../model/dto/api/cn-abgleich-request.dto';
import {CnAbgleichResultDto} from '../../../model/dto/api/cn-abgleich-result.dto';
import {CnActivityFilterDto} from '../../../model/dto/api/cn-activity-filter.dto';
import {CnActivityCountDto} from '../../../model/dto/api/cn-activity-count.dto';
import {CnActivityPageDto} from '../../../model/dto/api/cn-activity-page.dto';
import {CnDqCountersDto} from '../../../model/dto/api/cn-dq-counters.dto';
import {CnActivityTotalsDto} from '../../../model/dto/api/cn-activity-totals.dto';
import {CnControllingKpiDto} from '../../../model/dto/api/cn-controlling-kpi.dto';
import {CnBulkAcknowledgeResultDto} from '../../../model/dto/api/cn-bulk-acknowledge-result.dto';
import {CnActivityTrendDto} from '../../../model/dto/api/cn-activity-trend.dto';
import {CnActivityByPartnerDto} from '../../../model/dto/api/cn-activity-by-partner.dto';
import {CommissionAcknowledgeStatusEnum} from '../../../model/protected-domain/enum/commission-acknowledge-status/commission-acknowledge-status.enum';
import {CommissionActivityKindEnum} from '../../../model/protected-domain/enum/commission-activity-kind/commission-activity-kind.enum';
import {CommissionStatementDto} from '../../../model/dto/entity/commission-statement/commission-statement.dto';
import {
    CommissionStatementLineDto
} from '../../../model/dto/entity/commission-statement-line/commission-statement-line.dto';
import {CommissionActivityDto} from '../../../model/dto/entity/commission-activity/commission-activity.dto';

/**
 * API service for the cn commission statement, reconciliation, and activity REST endpoints.
 *
 * Endpoint mapping:
 *   POST /cn/commission-statement                → createStatement()
 *   POST /cn/commission-statement/{id}/line      → addLine()
 *   POST /cn/reconciliation/run                  → runReconciliation()
 *   POST /cn/reconciliation/abgleich             → abgleichVsModels()
 *   GET  /cn/activity                            → listActivities()
 *   GET  /cn/activity/page                       → listActivitiesPaged()
 *   GET  /cn/activity/count                      → countActivities()
 *   POST /cn/activity/{id}/acknowledge           → acknowledgeActivity()
 *   POST /cn/activity/acknowledge-bulk           → acknowledgeBulk()
 *   GET  /cn/activity/totals                     → getActivityTotals()
 *   GET  /cn/activity/totals/by-partner          → getActivityTotalsByPartner()
 *   GET  /cn/activity/totals/trend               → getTrend()
 *   GET  /cn/activity/controlling                → getControllingKpis()
 *   GET  /cn/dq/counters                         → getGlobalDqCounters()
 */
@Injectable({
    providedIn: 'root'
})
export class CnReconciliationApiService {

    private readonly statementBaseUrl = MvsCrudService.baseUrl + '/cn/commission-statement';
    private readonly reconciliationBaseUrl = MvsCrudService.baseUrl + '/cn/reconciliation';
    private readonly activityBaseUrl = MvsCrudService.baseUrl + '/cn/activity';
    private readonly dqBaseUrl = MvsCrudService.baseUrl + '/cn/dq';

    constructor(private http: HttpClient) {
    }

    /**
     * POST /cn/commission-statement
     * Creates a new CommissionStatement for the given partner and period.
     */
    createStatement(request: CnCreateStatementRequestDto): Observable<CommissionStatementDto> {
        return this.http.post<CommissionStatementDto>(
            this.statementBaseUrl,
            request
        );
    }

    /**
     * POST /cn/commission-statement/{id}/line
     * Appends one CommissionStatementLine to an existing statement.
     */
    addLine(statementId: number, request: CnAddStatementLineRequestDto): Observable<CommissionStatementLineDto> {
        return this.http.post<CommissionStatementLineDto>(
            `${this.statementBaseUrl}/${statementId}/line`,
            request
        );
    }

    /**
     * POST /cn/reconciliation/run
     * Triggers the automated reconciliation engine for a given partner and period.
     * Returns a result summary with per-status counters.
     */
    runReconciliation(request: CnRunReconciliationRequestDto): Observable<CnReconciliationResultDto> {
        return this.http.post<CnReconciliationResultDto>(
            `${this.reconciliationBaseUrl}/run`,
            request
        );
    }

    /**
     * POST /cn/reconciliation/abgleich
     * Compares received statement amounts against an active model and one or more scenario
     * models over a configurable forecast horizon.
     * TM-1000001915 (B12): the backend returns a SINGLE CnAbgleichResultDto (received total +
     * per-model expected total/delta), not an array.
     */
    abgleichVsModels(request: CnAbgleichRequestDto): Observable<CnAbgleichResultDto> {
        return this.http.post<CnAbgleichResultDto>(
            `${this.reconciliationBaseUrl}/abgleich`,
            request
        );
    }

    /**
     * GET /cn/activity
     * Returns all CommissionActivity records matching the given filter.
     * All filter parameters are optional — omit to retrieve all activities.
     *
     * BE-5 / TM-1000001915: acknowledgedFrom, acknowledgedTo, acknowledgedBy are appended when
     * present in the filter, enabling protocol audit search (Hans-Jürgen use-case).
     */
    listActivities(filter: CnActivityFilterDto): Observable<CommissionActivityDto[]> {
        let params = new HttpParams();
        if (filter.acknowledgeStatus !== undefined && filter.acknowledgeStatus !== null) {
            // TM-1000001915 (B18): the backend binds CommissionAcknowledgeStatus by enum NAME
            // (OPEN/ACKNOWLEDGED), so a numeric enum value (e.g. ACKNOWLEDGED=1) must be sent as
            // its name, not the ordinal '1' (which 400s). Callers may pass either form.
            const ack = typeof filter.acknowledgeStatus === 'number'
                ? CommissionAcknowledgeStatusEnum[filter.acknowledgeStatus]
                : filter.acknowledgeStatus;
            params = params.set('acknowledgeStatus', String(ack));
        }
        if (filter.partnerId !== undefined && filter.partnerId !== null) {
            params = params.set('partnerId', String(filter.partnerId));
        }
        if (filter.kind !== undefined && filter.kind !== null) {
            // B18: same name-vs-ordinal rule for CommissionActivityKind.
            const knd = typeof filter.kind === 'number'
                ? CommissionActivityKindEnum[filter.kind]
                : filter.kind;
            params = params.set('kind', String(knd));
        }
        // BE-5: protocol audit filters — append when provided.
        if (filter.acknowledgedFrom !== undefined && filter.acknowledgedFrom !== null) {
            params = params.set('acknowledgedFrom', filter.acknowledgedFrom);
        }
        if (filter.acknowledgedTo !== undefined && filter.acknowledgedTo !== null) {
            params = params.set('acknowledgedTo', filter.acknowledgedTo);
        }
        if (filter.acknowledgedBy !== undefined && filter.acknowledgedBy !== null && filter.acknowledgedBy !== '') {
            params = params.set('acknowledgedBy', filter.acknowledgedBy);
        }
        return this.http.get<CommissionActivityDto[]>(
            this.activityBaseUrl,
            {params}
        );
    }

    /**
     * GET /cn/activity/page
     * Returns ONE page of CommissionActivity records as a paged envelope (content + totalElements),
     * for the server-paged inbox lazy table (TM-1000001915 (4)). Reuses the same facets as
     * {@link listActivities} plus page / size / sortField / sortOrder. Separate from the unpaged list
     * endpoint so the Quittierungsprotokoll (full list) is unaffected.
     *
     * @param filter the activity facets PLUS page / size / sortField / sortOrder
     */
    listActivitiesPaged(filter: CnActivityFilterDto): Observable<CnActivityPageDto> {
        let params = new HttpParams();
        if (filter.acknowledgeStatus !== undefined && filter.acknowledgeStatus !== null) {
            const ack = typeof filter.acknowledgeStatus === 'number'
                ? CommissionAcknowledgeStatusEnum[filter.acknowledgeStatus]
                : filter.acknowledgeStatus;
            params = params.set('acknowledgeStatus', String(ack));
        }
        if (filter.partnerId !== undefined && filter.partnerId !== null) {
            params = params.set('partnerId', String(filter.partnerId));
        }
        if (filter.kind !== undefined && filter.kind !== null) {
            const knd = typeof filter.kind === 'number'
                ? CommissionActivityKindEnum[filter.kind]
                : filter.kind;
            params = params.set('kind', String(knd));
        }
        if (filter.acknowledgedFrom !== undefined && filter.acknowledgedFrom !== null) {
            params = params.set('acknowledgedFrom', filter.acknowledgedFrom);
        }
        if (filter.acknowledgedTo !== undefined && filter.acknowledgedTo !== null) {
            params = params.set('acknowledgedTo', filter.acknowledgedTo);
        }
        if (filter.acknowledgedBy !== undefined && filter.acknowledgedBy !== null && filter.acknowledgedBy !== '') {
            params = params.set('acknowledgedBy', filter.acknowledgedBy);
        }
        if (filter.period !== undefined && filter.period !== null && filter.period !== '') {
            params = params.set('period', filter.period);
        }
        if (filter.page !== undefined && filter.page !== null) {
            params = params.set('page', String(filter.page));
        }
        if (filter.size !== undefined && filter.size !== null) {
            params = params.set('size', String(filter.size));
        }
        if (filter.sortField !== undefined && filter.sortField !== null && filter.sortField !== '') {
            params = params.set('sortField', filter.sortField);
        }
        if (filter.sortOrder !== undefined && filter.sortOrder !== null) {
            params = params.set('sortOrder', String(filter.sortOrder));
        }
        return this.http.get<CnActivityPageDto>(
            `${this.activityBaseUrl}/page`,
            {params}
        );
    }

    /**
     * GET /cn/activity/count
     * Returns the count of CommissionActivity records matching the given status filter, optionally
     * narrowed by the same Periode + Gesellschaft scope the inbox tiles use (TM-1000001915 (4)).
     * Omitting partnerId / period reproduces the prior global count (backward compatible).
     *
     * @param status    the acknowledge status to count (OPEN / ACKNOWLEDGED)
     * @param partnerId optional partner (Gesellschaft) scope
     * @param period    optional YearMonth "YYYY-MM" scope
     */
    countActivities(status: string, partnerId?: number, period?: string): Observable<CnActivityCountDto> {
        let params = new HttpParams().set('status', status);
        if (partnerId !== undefined && partnerId !== null) {
            params = params.set('partnerId', String(partnerId));
        }
        if (period !== undefined && period !== null && period !== '') {
            params = params.set('period', period);
        }
        return this.http.get<CnActivityCountDto>(
            `${this.activityBaseUrl}/count`,
            {params}
        );
    }

    /**
     * POST /cn/activity/{id}/acknowledge
     * Acknowledges the given CommissionActivity. The server records the authenticated principal as
     * the acknowledgedBy stamp — no userName is sent from the client (BE-2 / TM-1000001915).
     * An optional note is persisted on the activity. Returns the updated activity with status
     * ACKNOWLEDGED.
     *
     * @param activityId  the id of the activity to acknowledge
     * @param note        optional free-text Quittierungsnotiz
     */
    acknowledgeActivity(activityId: number, note?: string): Observable<CommissionActivityDto> {
        const body: {note?: string} = note !== undefined ? {note} : {};
        return this.http.post<CommissionActivityDto>(
            `${this.activityBaseUrl}/${activityId}/acknowledge`,
            body
        );
    }

    /**
     * POST /cn/activity/acknowledge-bulk
     * Acknowledges multiple CommissionActivity records in a single call. Already-acknowledged
     * activities are skipped (idempotent). Returns the count of newly acknowledged and skipped
     * records.
     *
     * BE-2 / TM-1000001915: userName removed — the server derives acknowledgedBy from the
     * authenticated principal.
     *
     * @param ids   list of activity ids to acknowledge
     * @param note  optional free-text Quittierungsnotiz applied to all acknowledged activities
     */
    acknowledgeBulk(ids: number[], note?: string): Observable<CnBulkAcknowledgeResultDto> {
        const body: {ids: number[]; note?: string} = {
            ids,
            ...(note !== undefined ? {note} : {})
        };
        return this.http.post<CnBulkAcknowledgeResultDto>(
            `${this.activityBaseUrl}/acknowledge-bulk`,
            body
        );
    }

    /**
     * GET /cn/activity/totals
     * Returns a provisionType × acknowledgeStatus EUR matrix (AP / BP / Sonstige / Gesamt,
     * each split into Offen and Quittiert) for the Provisionssummen dashboard card.
     *
     * @param period     optional single YearMonth filter (e.g. "2024-03"); omit for all periods
     * @param partnerId  optional partner (Gesellschaft) id filter; omit for all partners
     * @param fromPeriod optional inclusive range start YearMonth ("YYYY-MM"); omit for an open start
     * @param toPeriod   optional inclusive range end YearMonth ("YYYY-MM"); omit for an open end
     */
    getActivityTotals(period?: string, partnerId?: number, fromPeriod?: string, toPeriod?: string): Observable<CnActivityTotalsDto> {
        let params = new HttpParams();
        if (period !== undefined) {
            params = params.set('period', period);
        }
        if (partnerId !== undefined) {
            params = params.set('partnerId', String(partnerId));
        }
        if (fromPeriod !== undefined && fromPeriod !== null && fromPeriod !== '') {
            params = params.set('fromPeriod', fromPeriod);
        }
        if (toPeriod !== undefined && toPeriod !== null && toPeriod !== '') {
            params = params.set('toPeriod', toPeriod);
        }
        return this.http.get<CnActivityTotalsDto>(
            `${this.activityBaseUrl}/totals`,
            {params}
        );
    }

    /**
     * GET /cn/activity/totals/by-partner
     * Returns an ORDERED array (by partnerName asc, nulls last, then partnerId) of the AP / BP /
     * AP+BP / Gesamt EUR sums per Gesellschaft (partner) that has activity in the requested scope.
     * Backs the "Provisionen je Gesellschaft" cross-Gesellschaft overview card (TM-1000001915,
     * Jean-Marie). Empty array when no partner has activity. All EUR amounts are scale-2 BigDecimal,
     * money-kind scoped (NEW_COMMISSION + STORNO only) — NO calculation in the FE.
     *
     * @param period     optional single YearMonth filter (e.g. "2024-03"); omit for all periods
     * @param partnerId  optional partner (Gesellschaft) id filter; when present, partner read-access
     *                   is enforced server-side; omit for all partners
     * @param fromPeriod optional inclusive range start YearMonth ("YYYY-MM"); omit for an open start
     * @param toPeriod   optional inclusive range end YearMonth ("YYYY-MM"); omit for an open end
     */
    getActivityTotalsByPartner(period?: string, partnerId?: number, fromPeriod?: string, toPeriod?: string): Observable<CnActivityByPartnerDto[]> {
        let params = new HttpParams();
        if (period !== undefined) {
            params = params.set('period', period);
        }
        if (partnerId !== undefined) {
            params = params.set('partnerId', String(partnerId));
        }
        if (fromPeriod !== undefined && fromPeriod !== null && fromPeriod !== '') {
            params = params.set('fromPeriod', fromPeriod);
        }
        if (toPeriod !== undefined && toPeriod !== null && toPeriod !== '') {
            params = params.set('toPeriod', toPeriod);
        }
        return this.http.get<CnActivityByPartnerDto[]>(
            `${this.activityBaseUrl}/totals/by-partner`,
            {params}
        );
    }

    /**
     * GET /cn/activity/totals/trend
     * Returns a time-series of AP/BP/Gesamt totals for the last N months, plus a
     * current-vs-previous-period delta (EUR + %). Used for the compact trend card on the dashboard.
     *
     * BE-3 / FE-5 / TM-1000001915.
     *
     * @param months    number of months to include in the series (e.g. 6 or 12)
     * @param partnerId optional partner (Gesellschaft) id filter; omit for all partners
     */
    getTrend(months: number, partnerId?: number): Observable<CnActivityTrendDto> {
        let params = new HttpParams().set('months', String(months));
        if (partnerId !== undefined) {
            params = params.set('partnerId', String(partnerId));
        }
        return this.http.get<CnActivityTrendDto>(
            `${this.activityBaseUrl}/totals/trend`,
            {params}
        );
    }

    /**
     * GET /cn/activity/controlling
     * Returns the controlling KPI bar values (Vollständigkeits-Quote, Offen, Quittiert, Überfällig,
     * Heute quittiert, Ältester offener Vorgang) for the dashboard.
     *
     * @param period     optional single YearMonth filter (e.g. "2024-03"); omit for all periods
     * @param partnerId  optional partner (Gesellschaft) id filter; omit for all partners
     * @param fromPeriod optional inclusive range start YearMonth ("YYYY-MM"); omit for an open start
     * @param toPeriod   optional inclusive range end YearMonth ("YYYY-MM"); omit for an open end
     */
    getControllingKpis(period?: string, partnerId?: number, fromPeriod?: string, toPeriod?: string): Observable<CnControllingKpiDto> {
        let params = new HttpParams();
        if (period !== undefined) {
            params = params.set('period', period);
        }
        if (partnerId !== undefined) {
            params = params.set('partnerId', String(partnerId));
        }
        if (fromPeriod !== undefined && fromPeriod !== null && fromPeriod !== '') {
            params = params.set('fromPeriod', fromPeriod);
        }
        if (toPeriod !== undefined && toPeriod !== null && toPeriod !== '') {
            params = params.set('toPeriod', toPeriod);
        }
        return this.http.get<CnControllingKpiDto>(
            `${this.activityBaseUrl}/controlling`,
            {params}
        );
    }

    /**
     * GET /cn/dq/counters
     * Returns the data-quality counters (coverage covered/total, incompleteBaseCount, unmatchedCount)
     * that back the Datenqualität panel KPI tiles. Unlike the per-simulation counters, these are derived
     * from the whole persisted expectation stock, so the panel shows real numbers even when opened
     * without a simulation-run scope.
     *
     * TM-1000001915 (b): the optional period (YYYY-MM) + partnerId facets let the Datenqualität dashboard
     * card honour the global Periode + Gesellschaft filter. Both are additive — omitting them reproduces
     * the prior global counters (backward compatible).
     *
     * @param period    optional YearMonth filter (e.g. "2026-06"); omit for all periods
     * @param partnerId optional partner (Gesellschaft) id filter; omit for all partners
     */
    getGlobalDqCounters(period?: string, partnerId?: number | null): Observable<CnDqCountersDto> {
        let params = new HttpParams();
        if (period !== undefined && period !== null && period !== '') {
            params = params.set('period', period);
        }
        if (partnerId !== undefined && partnerId !== null) {
            params = params.set('partnerId', String(partnerId));
        }
        return this.http.get<CnDqCountersDto>(
            `${this.dqBaseUrl}/counters`,
            {params}
        );
    }

}
